import { Badge } from '@/components/ui/badge'
import { InfoNotice } from '@/components/patterns/info-notice'
import { Button } from '@/components/ui/button'
import { SegmentedControl, SegmentedControlItem } from '@/components/ui/segmented-control'
import { Switch } from '@/components/ui/switch'
import { ToggleButton } from '@/components/ui/toggle-button'
import { Typography } from '@/components/ui/typography'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import ModifierKey from '@/components/ui/modifier-key'
import { useGoogleMaps } from '@/hooks/use-google-maps'
import { getGoogleMapsMapId } from '@/lib/google-maps-api-key'
import { cn } from '@/lib/utils'
import { SquareDashedMousePointer } from 'lucide-react'
import { getDeliveryRouteSelectionId, getSafeDayjs } from './delivery-share.utils'
import {
  DELIVERY_ROUTE_MAP_STYLES,
  FITTED_MAP_PADDING,
  MAP_BUCKETS,
  MARKER_DRIVER_BORDER_COLOR,
  MARKER_DRIVER_COLOR,
  MARKER_DRIVER_GLYPH_COLOR,
  MARKER_ORIGIN_BORDER_COLOR,
  MARKER_ORIGIN_COLOR,
  MARKER_ORIGIN_GLYPH_COLOR,
  ORDERED_ROUTE_PREVIEW_DEBOUNCE_MS,
  SINGLE_LOCATION_ZOOM,
  formatMapAddress,
  getDeliveryRouteMapAddress,
  getDeliveryRouteMapBucket,
  getDeliveryRouteMapDefaultBuckets,
  getDeliveryRouteMapDriverLocationPosition,
  getDeliveryRouteMapFilteredOrdersWithPinnedOrders,
  getDeliveryRouteMapLocationKey,
  getDeliveryRouteMapLocationLabel,
  getDeliveryRouteMapLocationMarkerPosition,
  getDeliveryRouteMapLocationMarkerTitle,
  getDeliveryRouteMapLocationRouteSignature,
  getDeliveryRouteMapLocations,
  getDeliveryRouteMapPosition,
  getStoredShowOptimizedRoutePreference,
  rememberShowOptimizedRoutePreference,
} from './delivery-route-map-model'
import {
  getDeliveryRouteMapMarkerDisplayLabel,
  getDeliveryRouteMapMarkerHandledState,
  getDeliveryRouteMapMarkerLabel,
  getDeliveryRouteMapMarkerSelectionState,
  getDeliveryRouteMapMarkerTitle,
  getDeliveryRouteMapMarkerToggleActions,
  isDeliveryRouteMapMarkerHighlighted,
} from './delivery-route-map-markers'
import {
  applyClassicMarkerStyle,
  applyMarkerElementTone,
  buildDriverMarkerElement,
  buildMarkerElement,
  buildOriginMarkerElement,
  getClassicMarkerStyle,
} from './delivery-route-map-elements'
import {
  getDeliveryRouteMapOptimizedOrderIds,
  getDeliveryRouteMapOptimizedRouteCandidates,
  getDeliveryRouteMapOrderedRouteCandidate,
  getDeliveryRouteMapPositionGroups,
  getDeliveryRouteMapRouteDistanceMeters,
  getDeliveryRouteMapRouteDurationMillis,
  getDeliveryRouteMapSelectedRouteSignature,
  getDeliveryRouteMapSelectionBounds,
  getDeliveryRouteMapSelectionOrderIds,
  isDeliveryRouteMapNavigationOrder,
  isOrderBlockedByRouteLocation,
  isDeliveryRouteMapPositionInsideBounds,
  positionKey,
} from './delivery-route-map-routing'
import { useEffect, useMemo, useRef, useState } from 'react'

function fitDeliveryRouteMapBounds({ bounds, map }) {
  map.fitBounds(bounds, FITTED_MAP_PADDING)
}

export default function DeliveryRouteBuilderMap({
  anchorOrder = null,
  driverLocation = null,
  enabledBuckets,
  highlightedOrderIds = [],
  isReadOnly = false,
  onEnabledBucketsChange,
  onHoveredOrderIdsChange,
  onOptimizedRouteOrderChange,
  onOptimizedRouteSummaryChange,
  onSelectOrders,
  onToggleOrders,
  orders = [],
  onToggleOrder,
  pinnedOrderIds = [],
  routeOrderMode = 'optimized',
  selectedOrderIds = [],
}) {
  const mapRef = useRef(null)
  const mapInstanceRef = useRef(null)
  const hasFittedDriverLocationRef = useRef(false)
  const hasFittedDeliveryMarkersRef = useRef(false)
  const driverLocationMarkerRef = useRef(null)
  const markerByGroupKeyRef = useRef(new Map())
  const markersRef = useRef([])
  const originMarkersRef = useRef([])
  const routePolylinesRef = useRef([])
  const selectionDragStartRef = useRef(null)
  const selectionRectangleRef = useRef(null)
  const routesRef = useRef(null)
  const [internalEnabledBuckets, setInternalEnabledBuckets] = useState(() =>
    getDeliveryRouteMapDefaultBuckets(orders)
  )
  const activeEnabledBuckets = enabledBuckets ?? internalEnabledBuckets
  const [geocodedPositions, setGeocodedPositions] = useState({})
  const [geocodedLocationPositions, setGeocodedLocationPositions] = useState({})
  const geocodedPositionAddressByOrderIdRef = useRef(new Map())
  const geocodedLocationPositionAddressByKeyRef = useRef(new Map())
  const [isRectangleSelectMode, setIsRectangleSelectMode] = useState(false)
  const [showOptimizedRoute, setShowOptimizedRoute] = useState(
    getStoredShowOptimizedRoutePreference
  )
  const { maps, marker, geocoding, routes, isLoaded, error } = useGoogleMaps([
    'maps',
    'marker',
    'geocoding',
    'routes',
  ])
  const anchorOrderRef = useRef(anchorOrder)
  const driverLocationPositionRef = useRef(null)
  const isReadOnlyRef = useRef(isReadOnly)
  const onHoveredOrderIdsChangeRef = useRef(onHoveredOrderIdsChange)
  const onOptimizedRouteOrderChangeRef = useRef(onOptimizedRouteOrderChange)
  const onOptimizedRouteSummaryChangeRef = useRef(onOptimizedRouteSummaryChange)
  const onSelectOrdersRef = useRef(onSelectOrders)
  const onToggleOrderRef = useRef(onToggleOrder)
  const onToggleOrdersRef = useRef(onToggleOrders)
  const hoveredGroupKeyRef = useRef(null)
  const rectangleSelectModeRef = useRef(isRectangleSelectMode)
  const selectedOrderIdsRef = useRef(selectedOrderIds)
  const showOptimizedRouteRef = useRef(showOptimizedRoute)
  const highlightedOrderIdSet = useMemo(() => new Set(highlightedOrderIds), [highlightedOrderIds])
  const selectedOrderIdSet = useMemo(() => new Set(selectedOrderIds), [selectedOrderIds])
  const selectedRouteOrders = useMemo(() => {
    const orderById = new Map(orders.map((order) => [getDeliveryRouteSelectionId(order), order]))

    return selectedOrderIds.map((orderId) => orderById.get(orderId)).filter(Boolean)
  }, [orders, selectedOrderIds])
  const selectedNavigationOrders = useMemo(
    () => selectedRouteOrders.filter(isDeliveryRouteMapNavigationOrder),
    [selectedRouteOrders]
  )
  const driverLocationPosition = useMemo(
    () => getDeliveryRouteMapDriverLocationPosition(driverLocation),
    [driverLocation]
  )
  const driverLocationSignature = driverLocationPosition
    ? [
        Number(driverLocationPosition.lat).toFixed(6),
        Number(driverLocationPosition.lng).toFixed(6),
        driverLocation?.accuracy_meters ?? '',
        driverLocation?.updated_at ?? '',
      ].join(':')
    : ''
  const optimizedRouteInputsRef = useRef({
    geocodedPositions,
    originLocation: anchorOrder?.location,
    routeStartPosition: driverLocationPosition,
    selectedOrders: selectedNavigationOrders,
  })
  const selectedRouteOrdersSignature = useMemo(
    () => getDeliveryRouteMapSelectedRouteSignature(selectedNavigationOrders, geocodedPositions),
    [geocodedPositions, selectedNavigationOrders]
  )
  const routeOriginLocationSignature = getDeliveryRouteMapLocationRouteSignature(
    anchorOrder?.location
  )
  const routeStartPositionSignature = driverLocationPosition
    ? positionKey(driverLocationPosition)
    : ''
  const routeReadinessSignature = [
    Boolean(routes?.Route ?? globalThis.google?.maps?.routes?.Route),
    routes?.TravelMode?.DRIVING ?? globalThis.google?.maps?.TravelMode?.DRIVING ?? '',
    routes?.RoutingPreference?.TRAFFIC_UNAWARE ??
      globalThis.google?.maps?.routes?.RoutingPreference?.TRAFFIC_UNAWARE ??
      '',
  ].join('|')

  const visibleOrders = useMemo(
    () =>
      getDeliveryRouteMapFilteredOrdersWithPinnedOrders(
        orders,
        activeEnabledBuckets,
        pinnedOrderIds
      ),
    [activeEnabledBuckets, orders, pinnedOrderIds]
  )
  const visibleOrderGeocodeAddresses = useMemo(() => {
    const addresses = new Map()

    visibleOrders.forEach((order) => {
      const address = getDeliveryRouteMapAddress(order)

      if (address) {
        addresses.set(String(getDeliveryRouteSelectionId(order)), address)
      }
    })

    return addresses
  }, [visibleOrders])
  const visibleOrderGeocodeAddressesRef = useRef(visibleOrderGeocodeAddresses)
  const mapViewportResetSignature = useMemo(
    () =>
      orders
        .map((order) =>
          [
            getDeliveryRouteSelectionId(order),
            order.location?.id ?? '',
            order.delivery_address?.line1 ?? '',
            order.delivery_address?.line2 ?? '',
            order.delivery_address?.city ?? '',
            order.delivery_address?.state ?? '',
            order.delivery_address?.postal_code ?? '',
            order.delivery_address?.country ?? '',
            order.delivery_address?.lat ?? '',
            order.delivery_address?.lng ?? '',
          ].join('/')
        )
        .join('|'),
    [orders]
  )
  const visibleRouteLocations = useMemo(() => getDeliveryRouteMapLocations(orders), [orders])
  const visibleLocationGeocodeAddresses = useMemo(() => {
    const addresses = new Map()

    visibleRouteLocations.forEach((location) => {
      const address = formatMapAddress(location?.address)
      const key = getDeliveryRouteMapLocationKey(location)

      if (address && key) {
        addresses.set(key, address)
      }
    })

    return addresses
  }, [visibleRouteLocations])
  const visibleLocationGeocodeAddressesRef = useRef(visibleLocationGeocodeAddresses)
  const visibleLocationMarkers = useMemo(
    () =>
      visibleRouteLocations
        .map((location) => ({
          key: getDeliveryRouteMapLocationKey(location),
          location,
          position: getDeliveryRouteMapLocationMarkerPosition(location, geocodedLocationPositions),
        }))
        .filter((item) => item.key && item.position),
    [geocodedLocationPositions, visibleRouteLocations]
  )
  const visibleLocationMarkersSignature = useMemo(
    () =>
      visibleLocationMarkers
        .map((item) =>
          [
            item.key,
            getDeliveryRouteMapLocationLabel(item.location),
            Number(item.position.lat).toFixed(6),
            Number(item.position.lng).toFixed(6),
          ].join(':')
        )
        .join('|'),
    [visibleLocationMarkers]
  )

  const visibleMapOrders = useMemo(
    () =>
      visibleOrders.filter((order) =>
        Boolean(getDeliveryRouteMapPosition(order, geocodedPositions))
      ),
    [geocodedPositions, visibleOrders]
  )
  const visibleMapOrderGroups = useMemo(
    () => getDeliveryRouteMapPositionGroups(visibleMapOrders, geocodedPositions),
    [geocodedPositions, visibleMapOrders]
  )
  const visibleMapOrderGroupsRef = useRef(visibleMapOrderGroups)
  const visibleLocationMarkersRef = useRef(visibleLocationMarkers)
  const visibleMapOrderGroupsSignature = useMemo(
    () =>
      visibleMapOrderGroups
        .map((group) =>
          [
            group.key,
            Number(group.position.lat).toFixed(6),
            Number(group.position.lng).toFixed(6),
            group.orders
              .map((order) =>
                [
                  getDeliveryRouteSelectionId(order),
                  order.location?.id ?? '',
                  order.recipient_name ?? '',
                  order.is_delivered ? 'delivered' : '',
                  order.is_route_missed ? 'missed' : '',
                  getDeliveryRouteMapBucket(order),
                ].join('/')
              )
              .join(','),
          ].join(':')
        )
        .join('|'),
    [visibleMapOrderGroups]
  )
  const missingPositionOrders = useMemo(
    () =>
      visibleOrders.filter(
        (order) =>
          !getDeliveryRouteMapPosition(order, geocodedPositions) &&
          getDeliveryRouteMapAddress(order)
      ),
    [geocodedPositions, visibleOrders]
  )
  const missingLocationPositionItems = useMemo(
    () =>
      visibleRouteLocations
        .map((location) => ({
          address: formatMapAddress(location?.address),
          key: getDeliveryRouteMapLocationKey(location),
          location,
        }))
        .filter(
          (item) =>
            item.key &&
            item.address &&
            !getDeliveryRouteMapLocationMarkerPosition(item.location, geocodedLocationPositions) &&
            !Object.prototype.hasOwnProperty.call(geocodedLocationPositions, item.key)
        ),
    [geocodedLocationPositions, visibleRouteLocations]
  )
  const canRectangleSelect =
    !isReadOnly && isLoaded && Boolean(maps?.Rectangle ?? globalThis.google?.maps?.Rectangle)
  const isRectangleSelectionActive = canRectangleSelect && isRectangleSelectMode
  const handleShowOptimizedRouteChange = (checked) => {
    setShowOptimizedRoute(checked)
    rememberShowOptimizedRoutePreference(checked)
  }

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (
        !canRectangleSelect ||
        event.defaultPrevented ||
        event.repeat ||
        event.altKey ||
        event.shiftKey ||
        (!event.ctrlKey && !event.metaKey) ||
        event.key?.toLowerCase() !== 's'
      ) {
        return
      }

      event.preventDefault()
      setIsRectangleSelectMode((current) => !current)
    }

    window.addEventListener('keydown', handleKeyDown)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [canRectangleSelect])

  useEffect(() => {
    anchorOrderRef.current = anchorOrder
    driverLocationPositionRef.current = driverLocationPosition
    isReadOnlyRef.current = isReadOnly
    onHoveredOrderIdsChangeRef.current = onHoveredOrderIdsChange
    onOptimizedRouteOrderChangeRef.current = onOptimizedRouteOrderChange
    onOptimizedRouteSummaryChangeRef.current = onOptimizedRouteSummaryChange
    onSelectOrdersRef.current = onSelectOrders
    onToggleOrderRef.current = onToggleOrder
    onToggleOrdersRef.current = onToggleOrders
    optimizedRouteInputsRef.current = {
      geocodedPositions,
      originLocation: anchorOrder?.location,
      routeStartPosition: driverLocationPosition,
      selectedOrders: selectedNavigationOrders,
    }
    rectangleSelectModeRef.current = isRectangleSelectionActive
    routesRef.current = routes
    selectedOrderIdsRef.current = selectedOrderIds
    showOptimizedRouteRef.current = showOptimizedRoute
  }, [
    anchorOrder,
    driverLocationPosition,
    geocodedPositions,
    isReadOnly,
    isRectangleSelectionActive,
    onHoveredOrderIdsChange,
    onOptimizedRouteOrderChange,
    onOptimizedRouteSummaryChange,
    onSelectOrders,
    onToggleOrder,
    onToggleOrders,
    routes,
    selectedOrderIds,
    selectedNavigationOrders,
    showOptimizedRoute,
  ])

  useEffect(() => {
    visibleMapOrderGroupsRef.current = visibleMapOrderGroups
  }, [visibleMapOrderGroups])

  useEffect(() => {
    visibleOrderGeocodeAddressesRef.current = visibleOrderGeocodeAddresses

    setGeocodedPositions((current) => {
      let next = current

      Object.keys(current).forEach((orderId) => {
        const geocodedAddress = geocodedPositionAddressByOrderIdRef.current.get(orderId)
        const currentAddress = visibleOrderGeocodeAddresses.get(orderId)

        if (geocodedAddress && geocodedAddress === currentAddress) {
          return
        }

        if (next === current) {
          next = { ...current }
        }

        delete next[orderId]
        geocodedPositionAddressByOrderIdRef.current.delete(orderId)
      })

      return next
    })
  }, [visibleOrderGeocodeAddresses])

  useEffect(() => {
    visibleLocationGeocodeAddressesRef.current = visibleLocationGeocodeAddresses

    setGeocodedLocationPositions((current) => {
      let next = current

      Object.keys(current).forEach((key) => {
        const geocodedAddress = geocodedLocationPositionAddressByKeyRef.current.get(key)
        const currentAddress = visibleLocationGeocodeAddresses.get(key)

        if (geocodedAddress && geocodedAddress === currentAddress) {
          return
        }

        if (next === current) {
          next = { ...current }
        }

        delete next[key]
        geocodedLocationPositionAddressByKeyRef.current.delete(key)
      })

      return next
    })
  }, [visibleLocationGeocodeAddresses])

  useEffect(() => {
    visibleLocationMarkersRef.current = visibleLocationMarkers
  }, [visibleLocationMarkers])

  useEffect(() => {
    hasFittedDeliveryMarkersRef.current = false
    hasFittedDriverLocationRef.current = false
  }, [mapViewportResetSignature])

  useEffect(() => {
    hasFittedDriverLocationRef.current = false
  }, [routeStartPositionSignature])

  useEffect(() => {
    if (!isLoaded || !maps || !mapRef.current || mapInstanceRef.current) {
      return
    }

    const mapId = getGoogleMapsMapId('deliveryRoute')
    const renderingType =
      maps.RenderingType?.RASTER ?? globalThis.google?.maps?.RenderingType?.RASTER ?? 'RASTER'
    const mapTypeId =
      maps.MapTypeId?.ROADMAP ?? globalThis.google?.maps?.MapTypeId?.ROADMAP ?? 'roadmap'
    mapInstanceRef.current = new maps.Map(mapRef.current, {
      center: { lat: 39.8283, lng: -98.5795 },
      zoom: 4,
      ...(mapId ? { mapId } : { styles: DELIVERY_ROUTE_MAP_STYLES }),
      mapTypeId,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      clickableIcons: false,
      renderingType,
    })
  }, [isLoaded, maps])

  useEffect(() => {
    if (!isLoaded || !maps || missingPositionOrders.length === 0) {
      return
    }

    const Geocoder = geocoding?.Geocoder ?? globalThis.google?.maps?.Geocoder
    const okStatus = geocoding?.GeocoderStatus?.OK ?? 'OK'

    if (!Geocoder) {
      return
    }

    const geocoder = new Geocoder()

    missingPositionOrders.forEach((order) => {
      const address = getDeliveryRouteMapAddress(order)
      const orderId = String(getDeliveryRouteSelectionId(order))

      void geocoder.geocode({ address }, (results, status) => {
        if (visibleOrderGeocodeAddressesRef.current.get(orderId) !== address) {
          return
        }

        if (status !== okStatus || !results?.[0]?.geometry?.location) {
          geocodedPositionAddressByOrderIdRef.current.delete(orderId)
          setGeocodedPositions((current) => {
            if (!Object.prototype.hasOwnProperty.call(current, orderId)) {
              return current
            }

            const next = { ...current }
            delete next[orderId]

            return next
          })
          return
        }

        const location = results[0].geometry.location
        const lat = Number(location.lat())
        const lng = Number(location.lng())
        const position = Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null

        setGeocodedPositions((current) => {
          if (position) {
            geocodedPositionAddressByOrderIdRef.current.set(orderId, address)

            return {
              ...current,
              [orderId]: position,
            }
          }

          geocodedPositionAddressByOrderIdRef.current.delete(orderId)

          if (!Object.prototype.hasOwnProperty.call(current, orderId)) {
            return current
          }

          const next = { ...current }
          delete next[orderId]

          return next
        })
      })
    })
  }, [geocoding, isLoaded, maps, missingPositionOrders])

  useEffect(() => {
    if (!isLoaded || !maps || missingLocationPositionItems.length === 0) {
      return
    }

    const Geocoder = geocoding?.Geocoder ?? globalThis.google?.maps?.Geocoder
    const okStatus = geocoding?.GeocoderStatus?.OK ?? 'OK'

    if (!Geocoder) {
      return
    }

    const geocoder = new Geocoder()

    missingLocationPositionItems.forEach(({ address, key }) => {
      void geocoder.geocode({ address }, (results, status) => {
        if (visibleLocationGeocodeAddressesRef.current.get(key) !== address) {
          return
        }

        if (status !== okStatus || !results?.[0]?.geometry?.location) {
          geocodedLocationPositionAddressByKeyRef.current.delete(key)
          setGeocodedLocationPositions((current) => {
            if (!Object.prototype.hasOwnProperty.call(current, key)) {
              return current
            }

            const next = { ...current }
            delete next[key]

            return next
          })
          return
        }

        const location = results[0].geometry.location
        const lat = Number(location.lat())
        const lng = Number(location.lng())
        const position = Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null

        setGeocodedLocationPositions((current) => {
          if (position) {
            geocodedLocationPositionAddressByKeyRef.current.set(key, address)

            return {
              ...current,
              [key]: position,
            }
          }

          geocodedLocationPositionAddressByKeyRef.current.delete(key)

          if (!Object.prototype.hasOwnProperty.call(current, key)) {
            return current
          }

          const next = { ...current }
          delete next[key]

          return next
        })
      })
    })
  }, [geocoding, isLoaded, maps, missingLocationPositionItems])

  useEffect(() => {
    const map = mapInstanceRef.current

    originMarkersRef.current.forEach((mapMarker) => {
      if ('map' in mapMarker) {
        mapMarker.map = null
        return
      }

      mapMarker.setMap?.(null)
    })
    originMarkersRef.current = []

    if (!isLoaded || !map || !maps || visibleLocationMarkersRef.current.length === 0) {
      return
    }

    const AdvancedMarkerElement = map.getMapCapabilities?.().isAdvancedMarkersAvailable
      ? marker?.AdvancedMarkerElement
      : null
    const ClassicMarker = maps.Marker
    const circlePath = maps.SymbolPath?.CIRCLE ?? globalThis.google?.maps?.SymbolPath?.CIRCLE

    visibleLocationMarkersRef.current.forEach(({ location, position }) => {
      const title = getDeliveryRouteMapLocationMarkerTitle(location)

      if (AdvancedMarkerElement) {
        const content = buildOriginMarkerElement({ ariaLabel: title })
        const mapMarker = new AdvancedMarkerElement({
          map,
          position,
          title,
          content,
          gmpClickable: false,
          zIndex: 10,
        })

        originMarkersRef.current.push(mapMarker)
        return
      }

      if (!ClassicMarker) {
        return
      }

      const mapMarker = new ClassicMarker({
        map,
        position,
        title,
        ...(circlePath
          ? {
              icon: {
                path: circlePath,
                scale: 10,
                fillColor: MARKER_ORIGIN_COLOR,
                fillOpacity: 1,
                strokeColor: MARKER_ORIGIN_BORDER_COLOR,
                strokeWeight: 2,
              },
            }
          : {}),
        label: { text: 'F', color: MARKER_ORIGIN_GLYPH_COLOR },
        zIndex: 10,
      })

      originMarkersRef.current.push(mapMarker)
    })
  }, [isLoaded, maps, marker, visibleLocationMarkersSignature])

  useEffect(() => {
    const map = mapInstanceRef.current

    if (driverLocationMarkerRef.current) {
      if ('map' in driverLocationMarkerRef.current) {
        driverLocationMarkerRef.current.map = null
      } else {
        driverLocationMarkerRef.current.setMap?.(null)
      }

      driverLocationMarkerRef.current = null
    }

    if (!isLoaded || !map || !maps || !driverLocationPosition) {
      return
    }

    const AdvancedMarkerElement = map.getMapCapabilities?.().isAdvancedMarkersAvailable
      ? marker?.AdvancedMarkerElement
      : null
    const ClassicMarker = maps.Marker
    const circlePath = maps.SymbolPath?.CIRCLE ?? globalThis.google?.maps?.SymbolPath?.CIRCLE
    const updatedAt = driverLocation?.updated_at ? getSafeDayjs(driverLocation.updated_at) : null
    const title = updatedAt
      ? `Driver location - updated ${updatedAt.format('MMM D, h:mm A')}`
      : 'Driver location'

    if (AdvancedMarkerElement) {
      driverLocationMarkerRef.current = new AdvancedMarkerElement({
        map,
        position: driverLocationPosition,
        title,
        content: buildDriverMarkerElement({ ariaLabel: title }),
        gmpClickable: false,
        zIndex: 30,
      })
      return
    }

    if (!ClassicMarker) {
      return
    }

    driverLocationMarkerRef.current = new ClassicMarker({
      map,
      position: driverLocationPosition,
      title,
      ...(circlePath
        ? {
            icon: {
              path: circlePath,
              scale: 11,
              fillColor: MARKER_DRIVER_COLOR,
              fillOpacity: 1,
              strokeColor: MARKER_DRIVER_BORDER_COLOR,
              strokeWeight: 2,
            },
          }
        : {}),
      label: { text: 'D', color: MARKER_DRIVER_GLYPH_COLOR },
      zIndex: 30,
    })
  }, [driverLocation, driverLocationPosition, driverLocationSignature, isLoaded, maps, marker])

  useEffect(() => {
    const map = mapInstanceRef.current

    if (
      !isLoaded ||
      !maps ||
      !map ||
      !driverLocationPosition ||
      hasFittedDriverLocationRef.current
    ) {
      return
    }

    const currentBounds = map.getBounds?.()

    if (
      currentBounds &&
      isDeliveryRouteMapPositionInsideBounds(driverLocationPosition, currentBounds)
    ) {
      hasFittedDriverLocationRef.current = true
      return
    }

    const LatLngBounds = maps.LatLngBounds ?? globalThis.google?.maps?.LatLngBounds
    const groups = visibleMapOrderGroupsRef.current
    const locationMarkers = visibleLocationMarkersRef.current

    if (LatLngBounds && groups.length > 0) {
      const bounds = new LatLngBounds()

      groups.forEach((group) => bounds.extend(group.position))
      locationMarkers.forEach((item) => bounds.extend(item.position))
      bounds.extend(driverLocationPosition)
      fitDeliveryRouteMapBounds({ bounds, map })
      hasFittedDriverLocationRef.current = true
      return
    }

    map.panTo?.(driverLocationPosition)
    hasFittedDriverLocationRef.current = true
  }, [driverLocationPosition, driverLocationSignature, isLoaded, maps])

  useEffect(() => {
    const map = mapInstanceRef.current
    if (!isLoaded || !map || !maps) {
      return
    }
    const groups = visibleMapOrderGroupsRef.current

    markersRef.current.forEach((mapMarker) => {
      if ('map' in mapMarker) {
        mapMarker.map = null
        return
      }

      mapMarker.setMap?.(null)
    })
    markersRef.current = []
    markerByGroupKeyRef.current = new Map()
    if (hoveredGroupKeyRef.current) {
      hoveredGroupKeyRef.current = null
      onHoveredOrderIdsChangeRef.current?.([])
    }

    if (groups.length === 0) {
      return
    }

    const LatLngBounds = maps.LatLngBounds ?? globalThis.google?.maps?.LatLngBounds

    if (!LatLngBounds) {
      return
    }

    const bounds = new LatLngBounds()
    const renderedPositionKeys = new Set()
    let firstRenderedPosition = null
    const extendRenderedBounds = (position) => {
      if (!position) {
        return
      }

      bounds.extend(position)
      firstRenderedPosition ??= position
      renderedPositionKeys.add(positionKey(position))
    }
    const driverPosition = driverLocationPositionRef.current

    if (driverPosition) {
      extendRenderedBounds(driverPosition)
    }

    const AdvancedMarkerElement = map.getMapCapabilities?.().isAdvancedMarkersAvailable
      ? marker?.AdvancedMarkerElement
      : null
    const ClassicMarker = maps.Marker
    const circlePath = maps.SymbolPath?.CIRCLE ?? globalThis.google?.maps?.SymbolPath?.CIRCLE

    groups.forEach((group) => {
      const firstOrder = group.orders[0]
      const bucket = getDeliveryRouteMapBucket(firstOrder)
      const handledState = getDeliveryRouteMapMarkerHandledState(group)
      const fallbackLabel = getDeliveryRouteMapMarkerLabel(group)
      const label = getDeliveryRouteMapMarkerDisplayLabel({ fallbackLabel, handledState })
      const markerSelectedOrderIdSet = new Set(selectedOrderIdsRef.current)
      const { isPartiallySelected, isSelected } = getDeliveryRouteMapMarkerSelectionState(
        group,
        markerSelectedOrderIdSet
      )
      const isBlocked = group.orders.every((order) =>
        isOrderBlockedByRouteLocation(order, anchorOrderRef.current, markerSelectedOrderIdSet)
      )
      const title = getDeliveryRouteMapMarkerTitle({
        anchorOrder: anchorOrderRef.current,
        group,
        handledState,
        isBlocked,
        selectedOrderIdSet: markerSelectedOrderIdSet,
      })
      const handleMarkerClick = () => {
        if (isReadOnlyRef.current) {
          return
        }

        const actions = getDeliveryRouteMapMarkerToggleActions({
          group,
          anchorOrder: anchorOrderRef.current,
          selectedOrderIds: selectedOrderIdsRef.current,
        })

        if (actions.length === 0) {
          return
        }

        if (onToggleOrdersRef.current) {
          onToggleOrdersRef.current(actions)
          return
        }

        actions.forEach(({ checked, orderId }) => {
          onToggleOrderRef.current?.(orderId, checked)
        })
      }
      const handleMarkerMouseEnter = () => {
        hoveredGroupKeyRef.current = group.key
        onHoveredOrderIdsChangeRef.current?.(group.orders.map(getDeliveryRouteSelectionId))
      }
      const handleMarkerMouseLeave = () => {
        if (hoveredGroupKeyRef.current !== group.key) {
          return
        }

        hoveredGroupKeyRef.current = null
        onHoveredOrderIdsChangeRef.current?.([])
      }

      extendRenderedBounds(group.position)

      if (AdvancedMarkerElement) {
        const content = buildMarkerElement({
          ariaLabel: title,
          bucket,
          handledState,
          isBlocked,
          isPartiallySelected,
          isReadOnlyRoute: isReadOnlyRef.current,
          isSelected,
          label,
          onClick: handleMarkerClick,
          onMouseEnter: handleMarkerMouseEnter,
          onMouseLeave: handleMarkerMouseLeave,
        })
        const mapMarker = new AdvancedMarkerElement({
          map,
          position: group.position,
          title,
          content,
          gmpClickable: true,
          zIndex: 20,
        })

        markerByGroupKeyRef.current.set(group.key, {
          element: content,
          marker: mapMarker,
        })
        markersRef.current.push(mapMarker)
        return
      }

      if (!ClassicMarker) {
        return
      }

      const markerStyle = getClassicMarkerStyle({
        circlePath,
        isBlocked,
        handledState,
        isPartiallySelected,
        isReadOnlyRoute: isReadOnlyRef.current,
        isSelected,
        label,
      })
      const mapMarker = new ClassicMarker({
        map,
        position: group.position,
        title,
        ...(markerStyle.icon ? { icon: markerStyle.icon } : {}),
        ...(markerStyle.label ? { label: markerStyle.label } : {}),
        zIndex: 20,
      })

      mapMarker.addListener?.('click', () => {
        handleMarkerClick()
      })
      mapMarker.addListener?.('mouseover', handleMarkerMouseEnter)
      mapMarker.addListener?.('mouseout', handleMarkerMouseLeave)
      markerByGroupKeyRef.current.set(group.key, { marker: mapMarker })
      markersRef.current.push(mapMarker)
    })

    if (!hasFittedDeliveryMarkersRef.current) {
      hasFittedDeliveryMarkersRef.current = true

      if (renderedPositionKeys.size <= 1) {
        map.setCenter(firstRenderedPosition)
        map.setZoom(SINGLE_LOCATION_ZOOM)
        return
      }

      fitDeliveryRouteMapBounds({ bounds, map })
      hasFittedDriverLocationRef.current = Boolean(driverPosition)
    }
  }, [isLoaded, maps, marker, visibleMapOrderGroupsSignature])

  useEffect(() => {
    const circlePath = maps?.SymbolPath?.CIRCLE ?? globalThis.google?.maps?.SymbolPath?.CIRCLE

    visibleMapOrderGroups.forEach((group) => {
      const entry = markerByGroupKeyRef.current.get(group.key)
      const bucket = getDeliveryRouteMapBucket(group.orders[0])
      const handledState = getDeliveryRouteMapMarkerHandledState(group)
      const fallbackLabel = getDeliveryRouteMapMarkerLabel(group)
      const label = getDeliveryRouteMapMarkerDisplayLabel({ fallbackLabel, handledState })
      const { isPartiallySelected, isSelected } = getDeliveryRouteMapMarkerSelectionState(
        group,
        selectedOrderIdSet
      )
      const isBlocked = group.orders.every((order) =>
        isOrderBlockedByRouteLocation(order, anchorOrder, selectedOrderIdSet)
      )
      const isHighlighted = isDeliveryRouteMapMarkerHighlighted(group, highlightedOrderIdSet)
      const title = getDeliveryRouteMapMarkerTitle({
        anchorOrder,
        group,
        handledState,
        isBlocked,
        selectedOrderIdSet,
      })

      if (entry?.element) {
        entry.element.setAttribute('aria-label', title)
        entry.element.title = title
        applyMarkerElementTone(entry.element, {
          bucket,
          handledState,
          isBlocked,
          isHighlighted,
          isPartiallySelected,
          isReadOnlyRoute: isReadOnly,
          isSelected,
        })
      }

      if (entry?.marker && !entry?.element) {
        applyClassicMarkerStyle(
          entry.marker,
          getClassicMarkerStyle({
            circlePath,
            handledState,
            isBlocked,
            isPartiallySelected,
            isReadOnlyRoute: isReadOnly,
            isSelected,
            label,
          })
        )
      }

      entry?.marker?.setTitle?.(title)
      if (entry?.marker && 'title' in entry.marker) {
        entry.marker.title = title
      }
      entry?.marker?.setOpacity?.(isBlocked ? 0.55 : 1)
      entry?.marker?.setZIndex?.(isHighlighted ? 40 : 20)
      if (entry?.marker && 'zIndex' in entry.marker) {
        entry.marker.zIndex = isHighlighted ? 40 : 20
      }
    })
  }, [
    anchorOrder,
    highlightedOrderIdSet,
    isReadOnly,
    maps,
    selectedOrderIdSet,
    visibleMapOrderGroups,
  ])

  useEffect(() => {
    const map = mapInstanceRef.current
    const currentRoutes = routesRef.current
    const Route = currentRoutes?.Route ?? globalThis.google?.maps?.routes?.Route

    if (!isLoaded || !map || !Route) {
      if (
        error &&
        routeOrderMode === 'optimized' &&
        typeof onOptimizedRouteOrderChangeRef.current === 'function'
      ) {
        onOptimizedRouteOrderChangeRef.current(null)
      }
      return
    }

    const hasOptimizedRouteConsumer =
      typeof onOptimizedRouteOrderChangeRef.current === 'function' ||
      typeof onOptimizedRouteSummaryChangeRef.current === 'function'

    const travelMode =
      currentRoutes?.TravelMode?.DRIVING ?? globalThis.google?.maps?.TravelMode?.DRIVING
    const routingPreference =
      currentRoutes?.RoutingPreference?.TRAFFIC_UNAWARE ??
      globalThis.google?.maps?.routes?.RoutingPreference?.TRAFFIC_UNAWARE
    const {
      geocodedPositions: optimizedRouteGeocodedPositions,
      originLocation,
      routeStartPosition,
      selectedOrders,
    } = optimizedRouteInputsRef.current
    const sharedRouteCandidateOptions = {
      originLocation,
      routeStartPosition,
      selectedOrders,
      geocodedPositions: optimizedRouteGeocodedPositions,
      ...(travelMode ? { travelMode } : {}),
      ...(routingPreference ? { routingPreference } : {}),
    }
    const routeCandidates =
      routeOrderMode === 'ordered'
        ? [getDeliveryRouteMapOrderedRouteCandidate(sharedRouteCandidateOptions)].filter(Boolean)
        : getDeliveryRouteMapOptimizedRouteCandidates(sharedRouteCandidateOptions)

    const clearRoutePolylines = () => {
      routePolylinesRef.current.forEach((polyline) => {
        polyline.setMap?.(null)
      })
      routePolylinesRef.current = []
    }

    if (selectedOrders.length === 0) {
      clearRoutePolylines()
      if (routeOrderMode === 'optimized') {
        onOptimizedRouteOrderChangeRef.current?.([])
      }
      onOptimizedRouteSummaryChangeRef.current?.(null)
      return
    }

    if (!showOptimizedRoute && !hasOptimizedRouteConsumer) {
      clearRoutePolylines()
      return
    }

    if (routeCandidates.length === 0) {
      clearRoutePolylines()
      if (routeOrderMode === 'optimized') {
        onOptimizedRouteOrderChangeRef.current?.(null)
      }
      onOptimizedRouteSummaryChangeRef.current?.(null)
      return
    }

    let isCurrent = true
    const runRouteCalculation = () => {
      clearRoutePolylines()
      Promise.all(
        routeCandidates.map((candidate) =>
          Route.computeRoutes(candidate.request)
            .then(({ routes: generatedRoutes = [] }) => {
              const route = generatedRoutes[0]
              const orderedOrderIds =
                routeOrderMode === 'ordered'
                  ? candidate.orderedOrderIds
                  : getDeliveryRouteMapOptimizedOrderIds(candidate, route)

              if (!route || orderedOrderIds.length === 0) {
                return null
              }

              return {
                durationMillis: getDeliveryRouteMapRouteDurationMillis(route),
                distanceMeters: getDeliveryRouteMapRouteDistanceMeters(route),
                orderedOrderIds,
                route,
              }
            })
            .catch(() => null)
        )
      )
        .then((candidateResults) => {
          if (isCurrent) {
            const bestRoute = candidateResults
              .filter(Boolean)
              .sort(
                (left, right) =>
                  left.durationMillis - right.durationMillis ||
                  left.orderedOrderIds.length - right.orderedOrderIds.length
              )[0]

            if (!bestRoute) {
              clearRoutePolylines()
              if (routeOrderMode === 'optimized') {
                onOptimizedRouteOrderChangeRef.current?.(null)
              }
              onOptimizedRouteSummaryChangeRef.current?.(null)
              return
            }

            const polylines = showOptimizedRouteRef.current
              ? (bestRoute.route.createPolylines?.({
                  strokeOpacity: 1,
                  strokeWeight: 5,
                }) ?? [])
              : []

            polylines.forEach((polyline) => {
              polyline.setMap?.(map)
            })
            routePolylinesRef.current = polylines
            if (routeOrderMode === 'optimized') {
              onOptimizedRouteOrderChangeRef.current?.(bestRoute.orderedOrderIds)
            }
            onOptimizedRouteSummaryChangeRef.current?.({
              distanceMeters: bestRoute.distanceMeters,
              durationMillis: bestRoute.durationMillis,
            })
          }
        })
        .catch(() => {
          if (isCurrent) {
            clearRoutePolylines()
            if (routeOrderMode === 'optimized') {
              onOptimizedRouteOrderChangeRef.current?.(null)
            }
            onOptimizedRouteSummaryChangeRef.current?.(null)
          }
        })
    }
    const routeCalculationTimeout =
      routeOrderMode === 'ordered'
        ? globalThis.setTimeout(runRouteCalculation, ORDERED_ROUTE_PREVIEW_DEBOUNCE_MS)
        : null

    if (routeOrderMode !== 'ordered') {
      runRouteCalculation()
    }

    return () => {
      isCurrent = false
      if (routeCalculationTimeout !== null) {
        globalThis.clearTimeout(routeCalculationTimeout)
      }
      clearRoutePolylines()
    }
  }, [
    error,
    isLoaded,
    routeOrderMode,
    routeOriginLocationSignature,
    routeStartPositionSignature,
    routeReadinessSignature,
    selectedRouteOrdersSignature,
    showOptimizedRoute,
  ])

  useEffect(() => {
    const map = mapInstanceRef.current
    const Rectangle = maps?.Rectangle ?? globalThis.google?.maps?.Rectangle

    const restoreDefaultMapGestures = () => {
      map?.setOptions?.({
        draggable: true,
        draggableCursor: null,
        draggingCursor: null,
      })
    }
    const setSelectionIdleMapGestures = () => {
      map?.setOptions?.({
        draggable: true,
        draggableCursor: 'crosshair',
        draggingCursor: 'crosshair',
      })
    }
    const setSelectionDrawingMapGestures = () => {
      map?.setOptions?.({
        draggable: false,
        draggableCursor: 'crosshair',
        draggingCursor: 'crosshair',
      })
    }
    const clearSelectionRectangle = () => {
      selectionRectangleRef.current?.setMap?.(null)
      selectionRectangleRef.current = null
      selectionDragStartRef.current = null
    }

    if (!isLoaded || !map || !maps || !Rectangle || !isRectangleSelectionActive) {
      clearSelectionRectangle()
      restoreDefaultMapGestures()
      return
    }

    setSelectionIdleMapGestures()

    const setSelectionBounds = (bounds) => {
      if (!bounds) {
        return
      }

      if (!selectionRectangleRef.current) {
        selectionRectangleRef.current = new Rectangle({
          bounds,
          clickable: false,
          fillColor: '#2563eb',
          fillOpacity: 0.12,
          map,
          strokeColor: '#2563eb',
          strokeOpacity: 0.9,
          strokeWeight: 2,
        })
        return
      }

      selectionRectangleRef.current.setBounds?.(bounds)
    }

    const finishSelection = (bounds) => {
      const orderIdsToSelect = getDeliveryRouteMapSelectionOrderIds({
        anchorOrder: anchorOrderRef.current,
        bounds,
        groups: visibleMapOrderGroupsRef.current,
        selectedOrderIds: selectedOrderIdsRef.current,
      })

      if (orderIdsToSelect.length > 0) {
        if (onSelectOrdersRef.current) {
          onSelectOrdersRef.current(orderIdsToSelect)
        } else {
          orderIdsToSelect.forEach((orderId) => onToggleOrderRef.current?.(orderId, true))
        }
      }
    }

    const handleMouseDown = (event) => {
      const startLatLng = event?.latLng
      const mouseButton = event?.domEvent?.button

      if (
        !rectangleSelectModeRef.current ||
        !startLatLng ||
        (mouseButton !== undefined && mouseButton !== 0)
      ) {
        return
      }

      event.domEvent?.preventDefault?.()
      event.domEvent?.stopPropagation?.()
      setSelectionDrawingMapGestures()
      selectionDragStartRef.current = startLatLng
      setSelectionBounds(getDeliveryRouteMapSelectionBounds(startLatLng, startLatLng))
    }

    const handleMouseMove = (event) => {
      if (!selectionDragStartRef.current || !event?.latLng) {
        return
      }

      event.domEvent?.preventDefault?.()
      event.domEvent?.stopPropagation?.()
      setSelectionBounds(
        getDeliveryRouteMapSelectionBounds(selectionDragStartRef.current, event.latLng)
      )
    }

    const handleMouseUp = (event) => {
      if (!selectionDragStartRef.current || !event?.latLng) {
        clearSelectionRectangle()
        setSelectionIdleMapGestures()
        return
      }

      event.domEvent?.preventDefault?.()
      event.domEvent?.stopPropagation?.()
      const bounds = getDeliveryRouteMapSelectionBounds(selectionDragStartRef.current, event.latLng)

      clearSelectionRectangle()
      setSelectionIdleMapGestures()
      finishSelection(bounds)
    }

    const listeners = [
      map.addListener?.('mousedown', handleMouseDown),
      map.addListener?.('mousemove', handleMouseMove),
      map.addListener?.('mouseup', handleMouseUp),
    ].filter(Boolean)

    return () => {
      listeners.forEach((listener) => listener.remove?.())
      clearSelectionRectangle()
      restoreDefaultMapGestures()
    }
  }, [isLoaded, isRectangleSelectionActive, maps])

  const toggleBucket = (bucket) => {
    const nextEnabledBuckets = {
      ...activeEnabledBuckets,
      [bucket]: !activeEnabledBuckets[bucket],
    }

    if (onEnabledBucketsChange) {
      onEnabledBucketsChange(nextEnabledBuckets)
      return
    }

    setInternalEnabledBuckets(nextEnabledBuckets)
  }

  return (
    <section className="flex h-full min-h-0 flex-col gap-3 overflow-hidden rounded-lg border bg-background p-4">
      <div
        data-slot="delivery-route-map-controls"
        className="flex max-w-full shrink-0 flex-wrap items-center justify-between gap-2"
      >
        <div
          data-slot="delivery-route-map-route-controls"
          className="flex max-w-full flex-wrap items-center justify-start gap-2"
        >
          {!isReadOnly ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <ToggleButton
                  type="button"
                  pressed={isRectangleSelectionActive}
                  pressedVariant="secondary"
                  size="sm"
                  disabled={!canRectangleSelect}
                  className={cn(
                    'rounded-full',
                    isRectangleSelectionActive && 'border-primary/30 bg-primary/10 text-interaction'
                  )}
                  onClick={() => setIsRectangleSelectMode((current) => !current)}
                >
                  <SquareDashedMousePointer className="size-4" />
                  Select area
                </ToggleButton>
              </TooltipTrigger>
              <TooltipContent className="inline-flex items-center gap-0.5">
                Toggle select area (
                <ModifierKey className="inline leading-none [&:not(:first-child)]:mt-0" />
                +S)
              </TooltipContent>
            </Tooltip>
          ) : null}
          <label className="inline-flex min-h-8 shrink-0 items-center gap-2 rounded-full border border-border/70 bg-background/80 px-3 py-1.5">
            <Switch
              aria-label="Show navigation"
              checked={showOptimizedRoute}
              onCheckedChange={handleShowOptimizedRouteChange}
            />
            <span className="text-sm font-medium text-muted-foreground">Show navigation</span>
          </label>
        </div>
        <SegmentedControl data-delivery-route-map-day-filters="" className="ml-auto">
          {MAP_BUCKETS.map((bucket) => (
            <SegmentedControlItem
              key={bucket.id}
              type="button"
              size="sm"
              selected={activeEnabledBuckets[bucket.id]}
              className="h-auto min-h-10 min-w-[5.5rem] px-3 py-2 text-center whitespace-nowrap"
              onClick={() => toggleBucket(bucket.id)}
            >
              {bucket.label}
            </SegmentedControlItem>
          ))}
        </SegmentedControl>
      </div>

      {error ? (
        <InfoNotice compact tone="neutral" title="Map unavailable">
          Use the delivery list below.
        </InfoNotice>
      ) : (
        <div className="relative min-h-0 xl:flex-1">
          <div
            ref={mapRef}
            className={cn(
              'delivery-route-builder-map h-[28rem] min-h-80 overflow-hidden rounded-lg border bg-muted xl:h-full xl:min-h-0',
              !isLoaded && 'animate-pulse',
              isRectangleSelectionActive && 'cursor-crosshair'
            )}
          />

          {visibleOrders.length > visibleMapOrders.length ? (
            <Typography
              variant="label"
              tone="muted"
              className="absolute right-3 bottom-3 left-3 block rounded-md bg-background/95 px-3 py-2 shadow-sm ring-1 ring-foreground/10"
            >
              {visibleOrders.length - visibleMapOrders.length} deliver
              {visibleOrders.length - visibleMapOrders.length === 1 ? 'y needs' : 'ies need'} a
              mapped address and still appear in the list.
            </Typography>
          ) : null}
        </div>
      )}

      <div className="flex shrink-0 flex-wrap gap-2">
        {MAP_BUCKETS.map((bucket) => (
          <Badge key={bucket.id} variant="outline">
            {bucket.label}:{' '}
            {orders.filter((order) => getDeliveryRouteMapBucket(order) === bucket.id).length}
          </Badge>
        ))}
      </div>
    </section>
  )
}
